import type { MethodForModel, ModelName, ParamsForMethod, ReturnForMethod, SupportedSession } from "../types";
import { parseISOStrings } from "@tellescope/type-correction";
import { formResponsePredicate } from "./form-response-iso-parsing-predicate";

const METHODS_WITH_SIMPLE_ID_REQUEST: string[] = ["updateOne", "getOne"] satisfies MethodForModel<ModelName>[];

/**
 * Arguments required to execute a Tellescope API method.
 *
 * @template Model - The Tellescope model name (e.g., 'users', 'appointments')
 * @template Method - The API method for the specified model (e.g., 'getOne', 'getSome')
 *
 * @property session - The Tellescope SDK session instance containing the authenticated API client
 * @property model - The name of the model to perform the operation on
 * @property method - The API method to execute on the model
 * @property request - The parameters required for the specific API method
 * @property parseISOStrings - When true, automatically converts ISO 8601 date strings to Date objects in the response
 */
export interface ExecuteQueryArgs<Model extends ModelName, Method extends MethodForModel<Model>> {
  session: SupportedSession;
  model: Model;
  method: Method;
  request: ParamsForMethod<Model, Method>;
  parseISOStrings?: boolean;
}

/**
 * Executes a Tellescope API method dynamically based on the provided model and method names.
 *
 * This utility function provides type-safe access to the Tellescope SDK API methods,
 * validating that the method exists and is callable before execution. Optionally, it can
 * automatically convert ISO 8601 date strings in the response to Date objects.
 *
 * @template Model - The Tellescope model name
 * @template Method - The API method for the specified model
 *
 * @param args - The execution arguments containing session, model, method, request, and optional parseISOStrings flag
 * @param args.parseISOStrings - When true, converts ISO strings to Date objects with keepType=true, deep=true, deepArrays=true (default: false)
 * @returns A promise resolving to the API method's return value
 *
 * @throws {Error} If the specified method is not found for the model
 * @throws {Error} If the specified method is not a function
 *
 * @example
 * ```typescript
 * const result = await executeTellescopeApiMethod({
 *   session: tellescopeSession,
 *   model: 'users',
 *   method: 'getOne',
 *   request: { id: 'user123' }
 * });
 *
 * // With automatic date parsing
 * const resultWithDates = await executeTellescopeApiMethod({
 *   session: tellescopeSession,
 *   model: 'users',
 *   method: 'getOne',
 *   request: { id: 'user123' },
 *   parseISOStrings: true
 * });
 * ```
 */
export const executeTellescopeApiMethod = async <
  Model extends ModelName,
  Method extends MethodForModel<Model>,
  ReturnCount extends boolean = false,
>({
  session,
  model,
  method,
  request,
  parseISOStrings: shouldParseISOStrings = false,
}: ExecuteQueryArgs<Model, Method>): Promise<
  ReturnCount extends false ? ReturnForMethod<Model, Method> : { count: number }
> => {
  // @ts-expect-error - Dynamic access on union of Queries | EnduserQueries; model may only exist on one side. Runtime validation below handles missing models/methods.
  const modelApiMethod = session.api[model]?.[method];

  if (!modelApiMethod) {
    throw new Error(`Method ${String(method)} not found for model ${String(model)}`);
  }

  if (typeof modelApiMethod !== "function") {
    throw new Error(`API method ${String(method)} for model ${String(model)} is not a function`);
  }

  if (
    typeof method === "string" &&
    METHODS_WITH_SIMPLE_ID_REQUEST.includes(method) &&
    typeof request === "object" &&
    request !== null &&
    "id" in request
  ) {
    const { id, ...rest } = request;
    const { _options, ...minRequest } = { _options: {}, ...rest };
    const hasOptions =
      typeof _options === "object" && _options !== null ? !!Object.values(_options)?.length : !!_options;
    const hasRequest = !!Object.values(minRequest).length;
    let result: ReturnCount extends false ? ReturnForMethod<Model, Method> : { count: number };
    if (hasOptions) {
      result = await modelApiMethod(id, minRequest, _options);
    } else if (hasRequest) {
      result = await modelApiMethod(id, minRequest);
    } else {
      result = await modelApiMethod(id);
    }

    if (!shouldParseISOStrings) return result;
    return parseISOStrings(result, {
      keepType: true,
      deep: true,
      deepArrays: true,
      predicate: model === "form_responses" ? formResponsePredicate : undefined,
    });
  }

  // `createSome` accepts a bare array (handled by the fallback below) or the standardized
  // `{ items, _options }` object form, which we split back into the SDK's positional args.
  if (
    typeof method === "string" &&
    method === "createSome" &&
    typeof request === "object" &&
    request !== null &&
    !Array.isArray(request) &&
    "items" in request
  ) {
    const { items, _options } = request;
    const hasOptions =
      typeof _options === "object" && _options !== null ? !!Object.values(_options).length : !!_options;
    const result = hasOptions ? await modelApiMethod(items, _options) : await modelApiMethod(items);

    if (!shouldParseISOStrings) return result;
    return parseISOStrings(result, {
      keepType: true,
      deep: true,
      deepArrays: true,
      predicate: model === "form_responses" ? formResponsePredicate : undefined,
    });
  }

  const result = await modelApiMethod(request);
  if (!shouldParseISOStrings) return result;
  return parseISOStrings(result, {
    keepType: true,
    deep: true,
    deepArrays: true,
    predicate: model === "form_responses" ? formResponsePredicate : undefined,
  });
};
